/**
 * QUÉ TRABAJO DEL USUARIO SE PIERDE AL REARMAR LA HOJA.
 *
 * Rearmar vuelve a leer el documento desde cero: el servidor borra todos los renglones
 * (`DELETE FROM quote_concepts`) y los reconstruye desde la extracción. Es el botón más
 * destructivo de la aplicación y hasta ahora **no decía una palabra**.
 *
 * Y no se puede "conservar parte": al releer, los renglones se reconstruyen y las claves
 * pueden no coincidir —el documento pudo haber cambiado, y de hecho el archivo fuente de
 * un presupuesto se puede reemplazar—. Un análisis de IA pegado al concepto equivocado es
 * peor que no tenerlo: nadie lo ve hasta que ya se cotizó. Así que la decisión es binaria,
 * y lo único honesto es avisar bien antes.
 *
 * Lo que vive SOLO en esos renglones y por lo tanto se pierde:
 *   - los análisis con IA (`apu_ia_matrix`);
 *   - los renglones **corregidos a mano** (migr 085) — el caso mas delicado: existen
 *     porque el documento no se pudo leer bien y alguien comparó contra el original y
 *     escribió la descripción y la cantidad. No están en ninguna biblioteca;
 *   - la selección de qué conceptos cotizar (migr 049);
 *   - los interruptores de "cotizar con IA" (migr 065).
 *
 * Lo único recuperable son los análisis **aceptados**: el respaldo a la biblioteca global
 * ocurre solo al aceptar, así que al volver a generarlos se reusan a $0. Los demás se
 * pagan de nuevo, y de un rechazado se pierde el juicio del cotizador.
 *
 * Este módulo solo CUENTA. La decisión de mostrar u ocultar el aviso es de la pantalla.
 */

export interface ConceptoParaContar {
  kind?: string | null;
  hasApuIaMatrix?: boolean;
  apuIaReviewStatus?: 'pending' | 'approved' | 'rejected' | 'edited' | null;
  apuIaRequested?: boolean | null;
  cotizar?: boolean;
  editadoManual?: boolean;
}

export interface TrabajoEnRiesgo {
  /** Análisis aceptados: vuelven de la biblioteca a $0. */
  sinteticosAceptados: number;
  /** Análisis sin aceptar: se pierden y volver a generarlos se cobra. */
  sinteticosSinAceptar: number;
  /** Análisis rechazados: se pierde que el cotizador los rechazó. */
  sinteticosRechazados: number;
  /** Renglones que alguien corrigió a mano. Lo que NO se puede recuperar de ningún lado. */
  renglonesCorregidos: number;
  /** Conceptos que el cotizador dejó fuera del presupuesto a propósito. */
  conceptosNoSeleccionados: number;
  /** Interruptores de "cotizar con IA" encendidos a mano, todavía sin análisis. */
  marcadosParaIa: number;
}

export function contarTrabajoEnRiesgo(conceptos: readonly ConceptoParaContar[]): TrabajoEnRiesgo {
  const t: TrabajoEnRiesgo = {
    sinteticosAceptados: 0,
    sinteticosSinAceptar: 0,
    sinteticosRechazados: 0,
    renglonesCorregidos: 0,
    conceptosNoSeleccionados: 0,
    marcadosParaIa: 0,
  };
  for (const c of conceptos) {
    // Los agrupadores no son trabajo del usuario: se reconstruyen igual.
    if (c.kind === 'group') continue;

    if (c.hasApuIaMatrix) {
      if (c.apuIaReviewStatus === 'approved') t.sinteticosAceptados += 1;
      else if (c.apuIaReviewStatus === 'rejected') t.sinteticosRechazados += 1;
      else t.sinteticosSinAceptar += 1; // pending, edited, o sin revisar
    } else if (c.apuIaRequested === true) {
      // Marcado a mano para que la IA lo trabaje, pero todavía sin análisis: el
      // interruptor es una decisión del cotizador y también se borra.
      t.marcadosParaIa += 1;
    }

    if (c.editadoManual) t.renglonesCorregidos += 1;
    if (c.cotizar === false) t.conceptosNoSeleccionados += 1;
  }
  return t;
}

/** ¿Hay algo que perder? Si no, rearmar no tiene por qué preguntar nada. */
export function hayTrabajoEnRiesgo(t: TrabajoEnRiesgo): boolean {
  return (
    t.sinteticosAceptados > 0 ||
    t.sinteticosSinAceptar > 0 ||
    t.sinteticosRechazados > 0 ||
    t.renglonesCorregidos > 0 ||
    t.conceptosNoSeleccionados > 0 ||
    t.marcadosParaIa > 0
  );
}

/** Total de análisis con IA, para el encabezado del aviso. */
export function totalSinteticos(t: TrabajoEnRiesgo): number {
  return t.sinteticosAceptados + t.sinteticosSinAceptar + t.sinteticosRechazados;
}

/**
 * Las líneas del aviso, en español-MX y en orden de gravedad: primero lo que no se
 * recupera de ningún lado, al final lo que vuelve gratis.
 *
 * Cada línea dice QUÉ se pierde y QUÉ implica. Sin la segunda mitad, "4 análisis" no le
 * dice nada a nadie: lo que el cotizador necesita saber es si le va a costar dinero.
 */
export function lineasDelAviso(t: TrabajoEnRiesgo): string[] {
  const l: string[] = [];
  const p = (n: number, uno: string, varios: string) => `${n} ${n === 1 ? uno : varios}`;

  if (t.renglonesCorregidos > 0) {
    l.push(
      `${p(t.renglonesCorregidos, 'renglón que corregiste a mano', 'renglones que corregiste a mano')} — `
      + 'se pierden y hay que volver a escribirlos comparando contra el documento',
    );
  }
  if (t.sinteticosSinAceptar > 0) {
    l.push(
      `${p(t.sinteticosSinAceptar, 'análisis sin aceptar', 'análisis sin aceptar')} — `
      + 'se pierden, y volver a generarlos se cobra',
    );
  }
  if (t.sinteticosRechazados > 0) {
    l.push(
      `${p(t.sinteticosRechazados, 'análisis rechazado', 'análisis rechazados')} — `
      + 'se pierde que los habías rechazado',
    );
  }
  if (t.sinteticosAceptados > 0) {
    l.push(
      `${p(t.sinteticosAceptados, 'análisis aceptado', 'análisis aceptados')} — `
      + 'quedan en tu biblioteca; volver a generarlos no se cobra',
    );
  }
  if (t.conceptosNoSeleccionados > 0) {
    l.push(
      `${p(t.conceptosNoSeleccionados, 'concepto que dejaste fuera', 'conceptos que dejaste fuera')} — `
      + 'la hoja vuelve con todos seleccionados',
    );
  }
  if (t.marcadosParaIa > 0) {
    l.push(
      `${p(t.marcadosParaIa, 'concepto marcado para inteligencia artificial', 'conceptos marcados para inteligencia artificial')} — `
      + 'se apaga la marca',
    );
  }
  return l;
}
